import {
  Component,
  ElementRef,
  HostListener,
  inject,
  ViewChild,
} from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';
import { FormlyFieldSelectProps } from '@ngx-formly/core/select';
import { MultiSelect } from 'primeng/multiselect';
import { FORM_IMPORTS } from '../../_shared/_imports/form.import';

import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { DrawerModule } from 'primeng/drawer';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AutoFocusModule } from 'primeng/autofocus';
import { Skeleton } from 'primeng/skeleton';
import { AddressSearchPanelComponent } from '../../_shared/_components/address-search-panel/address-search-panel.component';
import { AddressSearchController, AddressSearchMode } from './address-search.controller';
import { AddressSearchProps } from '../../_services/address-cabina-search.service';

export interface MultiSelectFieldProps
  extends FormlyFieldProps, FormlyFieldSelectProps, AddressSearchProps {
  appendTo?: any;
  filter?: boolean;
  optionLabel?: string;
  optionValue?: string;
  tableOptions?: any[];
  group?: boolean;
  optionGroupLabel?: string;
  optionGroupChildren?: string;
  showClear?: boolean;
  nestedValue?: boolean;
  optionDescription?: string;
  filterBy?: string;
  showToggleAll?: boolean;
  translateLabel?: string;
  loading?: boolean;
}

@Component({
  selector: 'multiselect-field',
  standalone: true,
  imports: [
    CommonModule,
    ...FORM_IMPORTS,
    MultiSelect,
    IconFieldModule,
    InputIconModule,
    InputText,
    DrawerModule,
    TableModule,
    TranslatePipe,
    AutoFocusModule,
    Skeleton,
    AddressSearchPanelComponent,
  ],
  providers: [AddressSearchController],
  template: `
    @if (props.loading) {
      <p-skeleton borderRadius="8px" height="2.75rem" styleClass="w-full" />
    } @else {
      <p-multiSelect
        #dropdown
        [ngClass]="{ 'p-disabled': props.readonly }"
        (onClick)="handleDropdownClick()"
        dropdownIcon="fa-solid fa-caret-down"
        class="w-full"
        [appendTo]="props.appendTo ?? 'body'"
        [formControl]="formControl"
        [formlyAttributes]="field"
        [options]="$any(visibleOptions)"
        autocomplete="false"
        [placeholder]="props.placeholder"
        [virtualScroll]="true"
        [filter]="props.filter && !isAddressMode"
        [virtualScrollItemSize]="43"
        [optionLabel]="props.optionLabel ?? 'label'"
        [showToggleAll]="props.showToggleAll ?? true"
        [optionValue]="
          props.nestedValue ? undefined : (props.optionValue ?? 'value')
        "
        [group]="props.group"
        [optionGroupLabel]="props.optionGroupLabel ?? 'label'"
        [optionGroupChildren]="props.optionGroupChildren ?? 'items'"
        [filterBy]="props.filterBy ?? undefined"
        (onPanelHide)="onPanelHidden()"
      >
        <ng-template #header>
          @if (props.addressSearch && !props.group) {
            <app-address-search-panel
              [mode]="addressSearch.mode()"
              [query]="addressSearch.query()"
              [suggestions]="addressSearch.suggestions()"
              [loading]="addressSearch.loading()"
              [placesUnavailable]="addressSearch.placesUnavailable()"
              [resolved]="addressSearch.resolved()"
              (modeChange)="onAddressModeChange($event)"
              (queryChange)="addressSearch.onQueryChange($event)"
              (suggestionSelected)="addressSearch.selectSuggestion($event)"
            />
          }
        </ng-template>

        <ng-template #selecteditems let-selected>
          {{ getLabel(selected) }}
        </ng-template>

        <ng-template let-el #item>
          <div>
            @if (props.translateLabel) {
              {{
                props.translateLabel +
                  '.' +
                  (props.optionValue ? el[props.optionValue] : el) | translate
              }}
            } @else {
              {{ el[props.optionLabel ?? 'label'] }}
            }

            @if (props.optionDescription) {
              <div class="text-xs italic">
                {{ el[props.optionDescription] }}
              </div>
            }
          </div>
        </ng-template>
      </p-multiSelect>
    }

    <p-drawer
      [(visible)]="drawer"
      [position]="'bottom'"
      styleClass="h-auto min-h-[655px] pt-22"
      baseZIndex="1104"
      [maskStyle]="$any('z-index:1103')"
      (onShow)="onShow($event)"
    >
      <ng-template #closeicon>
        <i class="fa-solid fa-caret-up text-xl"></i>
      </ng-template>

      <ng-template #header>
        <div class="flex items-center gap-2">
          <span class="font-bold">{{
            props.label != '' ? props.label : props.placeholder
          }}</span>
        </div>
      </ng-template>

      <p-table
        #dt2
        scrollHeight="500px"
        [virtualScroll]="props.group ? false : true"
        [virtualScrollItemSize]="46"
        [value]="$any(props.tableOptions ?? visibleOptions)"
        selectionMode="multiple"
        [(selection)]="selected"
        [globalFilterFields]="
          $any(props.filterBy ?? props.optionLabel ?? 'label').split(',')
        "
        (selectionChange)="selectionChange($event)"
        [rowGroupMode]="props.group ? 'subheader' : undefined"
        [groupRowsBy]="props.group ? 'optionGroupLabel' : undefined"
        [sortField]="props.group ? 'optionGroupLabel' : undefined"
        sortMode="single"
        [dataKey]="props.group ? 'optionGroupLabel' : undefined"
      >
        @if (props.filter || props.addressSearch) {
          <ng-template #caption>
            @if (props.addressSearch && !props.group) {
              <app-address-search-panel
                [mode]="addressSearch.mode()"
                [query]="addressSearch.query()"
                [suggestions]="addressSearch.suggestions()"
                [loading]="addressSearch.loading()"
                [placesUnavailable]="addressSearch.placesUnavailable()"
                [resolved]="addressSearch.resolved()"
                (modeChange)="onDrawerAddressModeChange($event)"
                (queryChange)="addressSearch.onQueryChange($event)"
                (suggestionSelected)="addressSearch.selectSuggestion($event)"
              />
            }
            @if (props.filter && !isAddressMode) {
              <div class="flex">
                <p-iconfield iconPosition="left" class="w-full">
                  <input
                    #search
                    pInputText
                    [pAutoFocus]="true"
                    type="text"
                    (input)="
                      dt2.filterGlobal($any($event.target).value, 'contains')
                    "
                  /><p-inputicon>
                    <i class="fas fa-search"></i>
                  </p-inputicon>
                </p-iconfield>
              </div>
            }
          </ng-template>
        }

        @if (props.group) {
          <ng-template #groupheader let-row>
            <tr>
              <td colspan="2">
                <span class="font-bold">{{ row['optionGroupLabel'] }}</span>
              </td>
            </tr>
          </ng-template>
        }

        <ng-template #body let-el>
          <tr [pSelectableRow]="el">
            <td class="w-[20px]">
              <p-tableCheckbox [value]="el" />
            </td>
            <td>
              <div>
                @if (props.translateLabel) {
                  {{
                    props.translateLabel +
                      '.' +
                      (props.optionValue ? el[props.optionValue] : el)
                      | translate
                  }}
                } @else {
                  {{ el[props.optionLabel ? props.optionLabel : 'label'] }}
                }
              </div>
              @if (props.optionDescription) {
                <div class="text-xs italic">
                  {{ el[props.optionDescription] }}
                </div>
              }
            </td>
          </tr>
        </ng-template>

        <ng-template #emptyMessage>
          {{ 'primeng.emptyMessage' | translate }}
        </ng-template>
      </p-table>
    </p-drawer>
  `,
  styles: `
    :host ::ng-deep {
      p-multiselect {
        @apply w-full;
        display: flex !important;
        align-items: center !important;
        flex-wrap: nowrap !important;
        width: 100% !important;
      }
      p-multiselect > .p-multiselect-label-container {
        flex: 1 1 auto !important;
        min-width: 0 !important;
      }
      p-multiselect > .p-multiselect-clear-icon,
      p-multiselect > .p-multiselect-dropdown {
        flex: 0 0 auto !important;
      }
      p-multiselect > p-overlay {
        position: absolute !important;
      }
      p-overlay {
        @apply w-full;
      }
    }
  `,
})
export class MultiSelectFieldType extends FieldType<
  FieldTypeConfig<MultiSelectFieldProps>
> {
  protected readonly addressSearch = inject(AddressSearchController);
  private readonly _elementRef = inject(ElementRef<HTMLElement>);

  isMobile: boolean = false;
  @ViewChild('dt2') dt2!: any;
  @ViewChild('search', { static: false }) search?: ElementRef<HTMLInputElement>;
  @ViewChild('dropdown') private _dropdownRef?: MultiSelect;

  private readonly _translate = inject(TranslateService);

  /** Options bindate al dropdown: la lista completa, o solo la cabina risolta dall'indirizzo. */
  private _visibleSource: unknown[] = [];
  private _visibleCacheKey: unknown = null;
  private _visibleCache: unknown[] = [];

  get visibleOptions(): unknown[] {
    const source = (this.props.options ?? []) as Record<string, unknown>[];
    const matched = this.addressSearch.active() ? this.addressSearch.matched() : null;
    const key = `${this.addressSearch.active()}|${matched?.id ?? ''}|${source.length}|${this.formControl.value}`;
    if (this._visibleCacheKey !== key || this._visibleSource !== source) {
      this._visibleSource = source;
      this._visibleCacheKey = key;
      this._visibleCache = this.addressSearch.active()
        ? this._withSelectedPreserved(
            this.addressSearch.filterOptions(source, this.props.addressSearchMatchKeys ?? ['id', 'cod_ac']),
            source,
          )
        : source;
    }
    return this._visibleCache;
  }

  /**
   * 🔥 [options] è bindato anche per risolvere l'etichetta dei valori già
   * selezionati (#selecteditems): se la ricerca per indirizzo filtra via
   * un'opzione già selezionata, PrimeNG non trova più l'oggetto e il trigger
   * perde quella chip pur avendo ancora il valore nel form. La si ripristina
   * nell'elenco anche quando non risponde all'indirizzo cercato.
   */
  private _withSelectedPreserved<T extends Record<string, unknown>>(filtered: T[], source: T[]): T[] {
    const raw = this.formControl.value;
    const selected: unknown[] = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
    if (selected.length === 0) return filtered;
    const optionValueKey = this.props.nestedValue ? undefined : this.props.optionValue;
    const already = new Set(filtered);
    const preserved = source.filter((option) => {
      if (already.has(option)) return false;
      const optionKey = optionValueKey ? option[optionValueKey] : option;
      return selected.includes(optionKey);
    });
    return preserved.length ? [...filtered, ...preserved] : filtered;
  }

  get isAddressMode(): boolean {
    return !!this.props.addressSearch && this.addressSearch.mode() === 'address';
  }

  ngOnInit() {
    this.checkIfMobile();

    this.formControl.valueChanges.subscribe((value) => {
      if (!value || value.length === 0) {
        this.selected = [];
        this.dt2?.clear();
      }
    });
  }

  clearSelection() {
    this.selected = [];
    this.formControl.setValue([]);
    this.dt2?.clear();
  }

  @HostListener('window:resize', [])
  onResize() {
    this.checkIfMobile();
  }

  onAddressModeChange(mode: AddressSearchMode): void {
    // ⚠️ il filtro testuale nativo resta valorizzato anche quando l'input è
    // nascosto: va azzerato o la lista resterebbe filtrata sul testo precedente.
    this._dropdownRef?.resetFilter();
    this.addressSearch.setMode(mode);
    setTimeout(() => this._dropdownRef?.scroller?.scrollToIndex(0));
  }

  onDrawerAddressModeChange(mode: AddressSearchMode): void {
    this.dt2?.clear();
    this.addressSearch.setMode(mode);
  }

  onPanelHidden(): void {
    this.addressSearch.reset();
  }

  private checkIfMobile() {
    this.isMobile = window.innerWidth <= 768;
  }
  drawer: boolean = false;

  selected: any;

  handleDropdownClick() {
    if (this.props.readonly) {
      return;
    }

    if (this.isMobile) {
      this.drawer = !this.drawer;
      return;
    }

    this.ensureSpaceBelow();
  }

  /**
   * 🔥 PrimeNG apre il pannello sopra il trigger quando non c'è spazio sotto
   * nel viewport (comportamento interno, non configurabile): in dialog corti
   * questo lo fa sembrare staccato dal campo. Scrollando il trigger prima
   * dell'apertura si garantisce spazio sufficiente sotto, così il pannello si
   * apre sempre ancorato correttamente. `instant` per evitare che uno scroll
   * animato (es. `scroll-behavior: smooth` sull'ancestor) sia ancora in corso
   * quando PrimeNG calcola la posizione.
   */
  private ensureSpaceBelow(): void {
    const rect = this._elementRef.nativeElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow < 320) {
      this._elementRef.nativeElement.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  }

  getLabel(s: any[]) {
    const seleteds = s && s.length > 0 ? s : this.formControl.value;

    let strings = seleteds?.map((selected: any) => {
      if (this.props.translateLabel) {
        return this._translate.instant(
          this.props.translateLabel +
            '.' +
            (this.props.optionValue
              ? selected[this.props.optionValue]
              : selected),
        );
      } else {
        return selected[this.props.optionLabel ?? 'label'];
      }
    });
    return (strings ?? []).join(', ');
  }

  selectionChange(event: any[]) {
    if (this.props.readonly) {
      return;
    }
    if (!event || event.length === 0) {
      this.clearSelection();
      return;
    }

    let data = event;

    if (this.props.group) {
      const children = ((this.props.options ?? []) as any[]).flatMap(
        (g) => g[this.props.optionGroupChildren!],
      );

      data = event
        .map((e) => children.find((c) => c.id === e.id))
        .filter(Boolean);
    }

    if (this.props.optionValue && !this.props.nestedValue) {
      this.formControl.setValue(
        data.map((e: any) => e[this.props.optionValue!]),
      );
    } else {
      this.formControl.setValue(data);
    }

    this.selected = data;
  }

  onShow(event: any) {
    if (this.props.readonly) {
      this.drawer = false;
      return;
    }
    const options = this.props.options as any[];

    if (this.formControl.value) {
      // Se `formControl.value` è un array di ID
      const selectedValues = Array.isArray(this.formControl.value)
        ? this.formControl.value
        : [this.formControl.value];

      this.selected = options.filter((el) => {
        return selectedValues.includes(
          this.props.optionValue ? el[this.props.optionValue] : el,
        );
      });
      const firstIndex = options.findIndex((el) =>
        this.props.optionValue
          ? el[this.props.optionValue] == selectedValues[0]
          : el == selectedValues[0],
      );

      // Scrolla al primo selezionato
      setTimeout(() => {
        this.search?.nativeElement.focus();
        if (
          this.dt2?.scroller &&
          typeof this.dt2.scroller.scrollToIndex === 'function'
        ) {
          this.dt2.scroller.scrollToIndex(firstIndex);
        } else {
          console.warn('Scroller non disponibile o scrollToIndex non presente');
        }
      }, 0);
    }
  }
}
